<script setup>
import { onUpdated, ref, watch, onMounted } from "vue"
import { KNOLIA } from "@/knolia"
import { defineEmits, defineProps } from "vue"
import CommentBalloon from "./CommentBalloon.vue"
import { useUserStore } from "../store/modules/user"

const props = defineProps({
  comments: { type: [Array, Object], default: () => [] },
  element: { type: Object, required: true },
  mobileView: { type: Boolean, default: false },
  validateSkills: { type: Boolean, default: false },
  ratingSkillsModal: { type: Object, default: () => ({ ShowChatValidators: false }) },
  loggedUserName: { type: String, default: '' }
})
const emits = defineEmits(["get-comments", 'update:comments'])
const store = useUserStore()
let textAreaMessage = ref('');
let commentReply = ref([])

function ControlReply(comment = []) {
  commentReply.value = comment
}

function formatDateNow() {
  const d = new Date()
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} `
       + `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function normalizeComments(src) {
  if (Array.isArray(src)) return src.slice()
  // If it's an object with numeric keys (like Proxy(Object) 0: {...}), convert to values
  return Object.values(src || {})
}

function makeTempId() {
  return 'temp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8)
}

function CreateSenderBalloon(tempId) {
  const newMessage = {
    sender_id: KNOLIA.GetLoggedUserId(),
    photo_src: store.User.src,
    comment_id: tempId,
    sender_name: store.User.professional_name,
    comment: textAreaMessage.value,
    replied_to: commentReply.value.comment_id,
    replied_comment: commentReply.value,
    date: formatDateNow(),
  }

  const current = normalizeComments(props.comments)
  emits('update:comments', [...current, newMessage])
}

async function SendMessage() {
  if(textAreaMessage.value == '')
    return 

  const comment = textAreaMessage.value
  const replied_to = commentReply.value

  const tempId = makeTempId()
  CreateSenderBalloon(tempId)

  textAreaMessage.value = ''
  commentReply.value = []

  await KNOLIA.CallBackEnd("post", "set-operations&get=send-comment", {
    comment,
    replied_to: replied_to.comment_id,
    element: props.element,
    only_validators: props.ratingSkillsModal.ShowChatValidators
  }).then((res) => {

    if(res.data.skills_ratings_id)
      Object.assign(props.element, {skills_ratings_id: res.data.skills_ratings_id})

    if(res.data.new_comment_id) {
      let updated = normalizeComments(props.comments) // re-read current comments from props
      const idx = updated.findIndex(m => String(m.comment_id) === String(tempId))

      if (idx === -1)
        return

      const replaced = { ...updated[idx], comment_id: res.data.new_comment_id }
      updated = [...updated.slice(0, idx), replaced, ...updated.slice(idx + 1)]
      emits('update:comments', updated)
    }
  });
}

async function DeleteComment(commentId) {
  await KNOLIA.CallBackEnd("post", "set-operations&get=delete-comment", {
    commentId
  }).then(() => {
    emits('get-comments')
  });
}

function GoToComment(id) {
  const comment = document.getElementById(`comment${id}`)
  comment.scrollIntoView()
  comment.classList.add('replied-message')
}

onMounted(() => {
  if(props.ratingSkillsModal.ShowChat)
    textAreaMessage.value = props.ratingSkillsModal.TxtInput
  else if(props.ratingSkillsModal.ShowChatValidators)
    textAreaMessage.value = props.ratingSkillsModal.TxtInputValidators
})

onUpdated(() => {
  if(props.ratingSkillsModal.ShowChat)
    textAreaMessage.value = props.ratingSkillsModal.TxtInput
  else if(props.ratingSkillsModal.ShowChatValidators)
    textAreaMessage.value = props.ratingSkillsModal.TxtInputValidators
  
  const commentArea = document.getElementById('comment-area')
  commentArea.scrollTop = commentArea.scrollHeight
})

watch(textAreaMessage, (e) => {
  if(props.ratingSkillsModal.ShowChat)
    props.ratingSkillsModal.SetTxtInput(e)
  else if(props.ratingSkillsModal.ShowChatValidators)
    props.ratingSkillsModal.SetTxtInputValidators(e)
})
</script>

<template>
  <div class="div-comment-area">
    <div class="div-comment-container scrollbar" id="comment-area">
      <CommentBalloon :element="element" :comment="comment" :reply="false" :validate-skills="validateSkills" v-for="comment in props.comments" :key="comment.comment_id" @control-reply="ControlReply($event)" @delete-comment="DeleteComment($event)">
        <div v-if="Object.keys(comment.replied_comment).length > 0">
            <CommentBalloon :comment="comment.replied_comment" :replied-comment="true" @click="GoToComment(comment.replied_to)"/>
          </div>
      </CommentBalloon>
    </div>

    <div class="div-send-message-space">
      <div class="reply-comment" v-if="Object.keys(commentReply).length > 0">
        <CommentBalloon :comment="commentReply" :reply="true" @control-reply="ControlReply($event)"/>
      </div>

      <div class="div-send-message">
        <input type="text" v-model="textAreaMessage" class="textarea-comment" :class="{'ddl-alt-padding': props.mobileView}" maxlength="1500" />
        <button @click="SendMessage()" class="button-send-comment" :class="{'ddl-alt-padding': props.mobileView}">Enviar</button>
      </div>
    </div>
  </div>
</template>

<style scoped>

.div-comment-area {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.div-comment-container {
  padding: 5px 10px 10px 10px;
  background-color: var(--default-app-alt-line-color);
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  flex: 1;
}

.div-comment-container > section{
  margin-top: 10px;
  margin-bottom: 10px;
}

.div-send-message-space {
  margin: 10px 0px;
}

.div-send-message {
  display: flex;
  justify-content: space-between;
  padding: 0px 10px 0px 10px;
  gap: 10px;
}

.textarea-comment {
  flex: 5;
}

.reply-comment {
  padding-left: 15px;
}

.reply-comment section{
  margin-bottom: 10px;
}

.button-send-comment {
  flex: 1;
  line-height: 4px;
}
</style>
